import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_opscounsel-ai/artifacts/a8v8akpl_F86DF7B3-E743-4829-B779-387ACA6F68DC_L0_001%20-%20Edited.png";

const TermsPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <img 
            src={LOGO_URL} 
            alt="OpsCounsel™ AI" 
            className="h-8 w-auto object-contain"
          />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-foreground mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: December 2025</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using OpsCounsel™ AI (&ldquo;Service&rdquo;), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              OpsCounsel™ AI is an AI-powered operational guidance tool designed for Ambulatory Surgery Center (ASC) 
              administrators and healthcare operations leaders. The Service provides general operational best-practice 
              guidance and decision support.
            </p>
          </section>

          <section className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-amber-800 dark:text-amber-200 mb-4">3. Important Limitations</h2>
            <div className="space-y-4 text-amber-700 dark:text-amber-300">
              <p>
                <strong>NOT HIPAA-REGULATED:</strong> OpsCounsel™ AI is not designed to process, store, or transmit 
                Protected Health Information (PHI). We do not sign Business Associate Agreements (BAAs).
              </p>
              <p>
                <strong>NO PHI OR PERSONAL DATA:</strong> You agree NOT to enter any patient information, PHI, 
                personal identifiers, confidential HR data, or sensitive employee information into the Service.
              </p>
              <p>
                <strong>GUIDANCE ONLY:</strong> The Service provides operational guidance only. It does not provide 
                legal, medical, or HR advice and does not replace professional counsel, regulatory guidance, or 
                qualified expert consultation.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">4. User Responsibilities</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>You are responsible for ensuring that your use of the Service complies with all applicable laws and regulations.</li>
              <li>You agree not to input any PHI, patient names, Social Security numbers, or other protected personal information.</li>
              <li>You acknowledge that any operational decisions made based on the Service&apos;s output are your sole responsibility.</li>
              <li>You will not use the Service for any unlawful purpose or in violation of these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">5. Account and Subscription</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To access certain features, you may need to create an account and/or purchase a subscription. 
              You are responsible for maintaining the confidentiality of your account credentials.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Subscription fees are billed in advance on a monthly or annual basis.</li>
              <li>You may cancel your subscription at any time. Access continues until the end of the billing period.</li>
              <li>We offer a 14-day money-back guarantee for annual subscriptions.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">6. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service, including its design, features, and content (excluding user-generated content), 
              is owned by OpsCounsel™ AI and protected by intellectual property laws. You may not copy, 
              modify, distribute, or reverse engineer any part of the Service without prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE 
              SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">8. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, OPSCOUNSEL™ AI SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF 
              PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, 
              USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">9. Modifications to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of 
              material changes via email or through the Service. Continued use of the Service 
              after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">10. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the 
              United States, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">11. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at:
              <br />
              <a href="mailto:support@opscounsel.ai" className="text-primary hover:underline">
                support@opscounsel.ai
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-6 px-4">
        <div className="max-w-4xl mx-auto text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} OpsCounsel™ AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
